import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const lightNav = isHome && !scrolled;

  const navItems = [
    { name: 'About Us', path: '/about' },
    { name: 'Why Us', path: '/why-us' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Careers', path: '/careers' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link to="/" className="flex items-center">
              <img
                src="/images/analytical-prism-logo.svg"
                alt="Analytical Prism"
                className={`h-9 md:h-11 w-auto object-contain transition-all ${
                  lightNav ? 'brightness-0 invert' : ''
                }`}
              />
            </Link>
          </motion.div>

          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative"
              >
                <Link
                  to={item.path}
                  className={`text-sm font-medium tracking-wide transition-colors ${
                    isActive(item.path)
                      ? lightNav
                        ? 'text-white'
                        : 'text-[#0F2744]'
                      : lightNav
                        ? 'text-white/90 hover:text-white'
                        : 'text-slate-600 hover:text-[#0F2744]'
                  }`}
                >
                  {item.name}
                </Link>
                {isActive(item.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-cyan-500 rounded-full" />
                )}
              </motion.div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="/app/login"
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                lightNav
                  ? 'text-[#0F2744] bg-white hover:bg-slate-100 shadow-md shadow-black/10'
                  : 'text-white bg-gradient-to-r from-[#0F2744] to-[#1E3A5F] hover:from-[#1a3354] hover:to-[#254770] shadow-md shadow-slate-900/10'
              }`}
            >
              Sign In
            </a>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-lg ${
              lightNav ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'
            }`}
            aria-label="Toggle menu"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`lg:hidden border-t pb-4 ${
                lightNav ? 'border-white/20 bg-[#0F2744]/95' : 'border-slate-100 bg-white'
              }`}
            >
              <div className="py-3 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-3 py-2.5 rounded-lg text-base font-medium ${
                      lightNav
                        ? isActive(item.path)
                          ? 'text-white bg-white/10'
                          : 'text-white/90 hover:bg-white/10'
                        : isActive(item.path)
                          ? 'text-[#0F2744] bg-slate-50'
                          : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <a
                  href="/app/login"
                  onClick={() => setIsMenuOpen(false)}
                  className={`block text-center mt-2 px-4 py-3 rounded-lg font-semibold ${
                    lightNav
                      ? 'text-[#0F2744] bg-white'
                      : 'text-white bg-[#0F2744]'
                  }`}
                >
                  Sign In
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
